import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  CardHeader,
  CardFooter,
  Alert
} from "reactstrap"
import { Query } from "react-apollo"
import { FilePond, registerPlugin } from "react-filepond"
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation"
import FilePondPluginImagePreview from "filepond-plugin-image-preview"
import _, { flowRight as compose } from "lodash"
import { useMutation } from "@apollo/react-hooks"
import { withRouter } from "react-router"
import { withBasicNotify } from "components/notification"
import { Img } from "components/image"
import { Spinner, BarLoader } from "components/spinner"
import { BackButton } from "components/utility"
import { CREATE_PRODUCT, UPDATE_PRODUCT, GET_CATEGORIES } from "../../gql"

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview)

const ProductForm = (props) => {
  const defaultData = {
    name: "",
    productNumber: "",
    description: "",
    url: "",
    note: "",
    isEnable: true,
    category: "",
    subcategory: ""
  }

  const [localData, setLocalData] = useState({ ...defaultData })
  const [id, setId] = useState(null)
  const [
    updateObject,
    { loading: updateLoading, error: updateError }
  ] = useMutation(UPDATE_PRODUCT)
  const [
    createObject,
    { loading: createLoading, error: createError }
  ] = useMutation(CREATE_PRODUCT)
  const [photoUrl, setPhotoUrl] = useState(null)
  useEffect(() => {
    // Props to state
    if (props.data) {
      const categoryObject = _.omit(props.data.category, "__typename")
      const subcategoryObject = _.omit(props.data.subcategory, "__typename")
      setId(props.data.id)
      setPhotoUrl(props.data.photoUrl)
      setLocalData({
        ..._.omit(props.data, [
          "id",
          "sender",
          "price",
          "__typename",
          "photoUrl"
        ]),
        category: categoryObject.id,
        subcategory: subcategoryObject.id
      })
    }
  }, [props.data])

  const onFieldChange = (key, value) => {
    setLocalData({
      ...localData,
      [key]: value
    })
  }

  const updateData = async (id, data) => {
    await updateObject({
      variables: {
        id,
        input: {
          ...data,
          isEnable: data.isEnable
        }
      }
    })
    props.history.goBack()
  }

  const createData = async (data) => {
    await createObject({
      variables: {
        input: {
          ...data
        }
      }
    })
    props.history.goBack()
  }

  const checkRequire = () => {
    return (
      localData.name === "" ||
      localData.productNumber === "" ||
      localData.category === "" ||
      localData.subcategory === ""
    )
  }

  const renderSaveButton = () => {
    return (
      <Button
        color="primary"
        disabled={checkRequire()}
        onClick={() => {
          if (id) {
            updateData(id, localData)
          } else {
            createData(localData)
          }
        }}
      >
        送出
      </Button>
    )
  }

  const renderTextFormGroup = (
    label,
    fieldName,
    fieldType,
    isRequired,
    onFieldChange
  ) => {
    return (
      <FormGroup row>
        <Label for={fieldName}>
          {label} {isRequired && "*"}
        </Label>
        <Input
          type={fieldType}
          name={fieldName}
          id={fieldName}
          placeholder={label}
          value={localData[fieldName]}
          onChange={(e) => onFieldChange(fieldName, e.target.value)}
        />
      </FormGroup>
    )
  }

  const renderCategoryOptions = (data) => {
    return data?.categories?.data.map((category) => {
      const categoryName = category.name + (!category.isEnable && " (已停用)")
      return (
        <option key={category.id} value={category.id}>
          {categoryName}
        </option>
      )
    })
  }

  const renderSubcategoryOptions = (data) => {
    return (
      localData?.category &&
      data?.categories?.data
        .filter((category) => category.id === localData.category)
        .map((item) => {
          return item.subcategories.map((subcategory) => {
            const categoryName =
              subcategory.name + (!subcategory.isEnable && " (已停用)")
            return (
              <option value={subcategory.id} key={subcategory.id}>
                {categoryName}
              </option>
            )
          })
        })
    )
  }

  return (
    <div className="">
      <Card>
        <CardHeader>
          <BackButton left />
        </CardHeader>
        <CardBody>
          <Form
            style={{
              marginLeft: "10px",
              marginRight: "10px"
            }}
          >
            {renderTextFormGroup("名稱", "name", "text", true, onFieldChange)}
            {renderTextFormGroup(
              "編號",
              "productNumber",
              "text",
              true,
              onFieldChange
            )}
            {renderTextFormGroup(
              "描述",
              "description",
              "text",
              false,
              onFieldChange
            )}
            <Query query={GET_CATEGORIES}>
              {({ loading, error, data }) => {
                if (loading) {
                  return <Spinner />
                }

                if (error) {
                  return <Alert color="danger">錯誤訊息：{`${error}`}</Alert>
                }

                return (
                  <>
                    <FormGroup row>
                      <Label for="category">分類 *</Label>
                      <Input
                        type="select"
                        name="category"
                        id="category"
                        value={localData.category}
                        onChange={(e) => {
                          e.preventDefault()
                          setLocalData({
                            ...localData,
                            category: e.target.value,
                            subcategory: ""
                          })
                        }}
                      >
                        <option value="">請選擇分類</option>
                        {renderCategoryOptions(data)}
                      </Input>
                    </FormGroup>
                    <FormGroup row>
                      <Label for="subcategory">次分類 *</Label>
                      <Input
                        type="select"
                        name="subcategory"
                        id="subcategory"
                        value={localData.subcategory}
                        onChange={(e) =>
                          onFieldChange("subcategory", e.target.value)
                        }
                      >
                        <option value="">請選擇次分類</option>
                        {renderSubcategoryOptions(data)}
                      </Input>
                    </FormGroup>
                  </>
                )
              }}
            </Query>
            {renderTextFormGroup("連結", "url", "url", false, onFieldChange)}
            {renderTextFormGroup("備註", "note", "text", false, onFieldChange)}
            <FormGroup row style={{ marginBottom: "0" }}>
              <Label>圖片</Label>
            </FormGroup>
            <FormGroup row>
              {photoUrl && (
                <Img
                  style={{ maxWidth: "100%" }}
                  src={`http://${photoUrl}`}
                  name="產品圖片"
                />
              )}
            </FormGroup>
            <FilePond
              labelIdle='將檔案拖拉至此 或 <span class="filepond--label-action">瀏覽檔案</span>'
              allowMultiple={false}
              beforeAddFile={(item) => {
                return new Promise((resolve) => {
                  if (
                    item.file.type === "image/jpg" ||
                    item.file.type === "image/jpeg" ||
                    item.file.type === "image/png"
                  ) {
                    onFieldChange("file", item.file)
                    return resolve(true)
                  }
                  props.notify.warn("請上傳 jpeg, jpg, png 格式")
                  return resolve(false)
                })
              }}
              beforeRemoveFile={() => {
                onFieldChange("file", null)
              }}
            />
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={localData.isEnable}
                  onChange={(e) => {
                    onFieldChange("isEnable", e.target.checked)
                  }}
                />{" "}
                是否啟用
              </Label>
            </FormGroup>
          </Form>
        </CardBody>
        <CardFooter>
          <div>{renderSaveButton()}</div>
        </CardFooter>
        <BarLoader loading={updateLoading || createLoading} />
      </Card>
      {(updateError || createError) && (
        <Alert color="danger">
          錯誤訊息：{`${updateError || createError}`}
        </Alert>
      )}
    </div>
  )
}

export default compose(withBasicNotify, withRouter)(ProductForm)
